"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { countries } from "@/lib/country/registry";
import { getLiveCategoriesForCountry } from "@/lib/constants/categories";
import { categoryMap } from "@/lib/constants/navigation";
import BrandMark from "./BrandMark";
import CountryFlag from "@/components/ui/CountryFlag";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const pathname = usePathname();
  const pathCountryCode = pathname.split("/")[1]?.toLowerCase();
  const currentCountryCode = countries.some((c) => c.code === pathCountryCode) ? pathCountryCode : "us";

  // Only categories with >=1 live calculator for this country — a hardcoded
  // list linked dead /{cc}/{category} combos from every page (Ahrefs 404s /
  // noindex zombies). Same source of truth as generateStaticParams.
  const categories = getLiveCategoriesForCountry(currentCountryCode).map((key) => ({
    label: key === "savings" ? "Savings & Budget" : categoryMap[key] || key,
    path: `/${key}`,
  }));

  // Deep-link into the compare directory's category filter. Values MUST match
  // the normalized `category` field in src/data/comparisons/*.json — note there
  // is no "Mortgage" category (mortgage comparisons live under "Loans").
  const compareLinks = [
    { label: "All Comparisons", href: "/compare" },
    { label: "Investing", href: "/compare?category=Investment" },
    { label: "Retirement", href: "/compare?category=Retirement" },
    { label: "Insurance", href: "/compare?category=Insurance" },
    { label: "Tax", href: "/compare?category=Tax" },
    { label: "Loans & Mortgage", href: "/compare?category=Loans" },
  ];

  const legalLinks = [
    { label: "About Us", href: "/about" },
    { label: "Contact Us", href: "/contact" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Financial Disclaimer", href: "/disclaimer" },
  ];

  return (
    <footer className="footer-bg">
      <div className="container footer-container">
        <div className="footer-columns">
          {/* Logo & About Info */}
          <div className="footer-column brand-column">
            <BrandMark variant="footer" />
            <p className="brand-description">
              Sleek, free financial estimators localized for 10 countries. Built to help you compare scenarios before making financial choices.
            </p>
            {/* The blog is the site's strongest content section, so surface it
                site-wide in the footer for internal-link equity + discovery. */}
            <Link href="/blog" className="footer-link footer-link-bold">
              Read the Personal Finance Blog →
            </Link>
          </div>

          {/* Calculator Categories */}
          <div className="footer-column">
            <h2 className="column-title">Calculators</h2>
            <ul className="footer-links">
              {categories.map((cat) => (
                <li key={cat.label}>
                  <Link href={`/${currentCountryCode}${cat.path}`} className="footer-link" aria-label={`${cat.label} calculators`}>
                    {cat.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compare Section */}
          <div className="footer-column">
            <h2 className="column-title">Compare</h2>
            <ul className="footer-links">
              {compareLinks.map((link) => (
                <li key={link.label}>
                  <Link href={link.href} className="footer-link" aria-label={link.href === "/compare" ? "All comparisons" : `${link.label} comparisons`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Daily Challenge Section */}
          <div className="footer-column">
            <h2 className="column-title">Daily Challenge</h2>
            <ul className="footer-links">
              <li>
                <Link href="/daily" className="footer-link">
                  Daily Hub
                </Link>
              </li>
              <li>
                <Link href="/daily/word" className="footer-link">
                  FinWord Puzzle
                </Link>
              </li>
              <li>
                <Link href="/daily/quiz" className="footer-link">
                  Finance Quiz
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal / Resources */}
          <div className="footer-column">
            <h2 className="column-title">Legal</h2>
            <ul className="footer-links">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <Link href={link.href} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-consent"))}
                  className="footer-link footer-cookie-btn"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>

          {/* Supported Countries */}
          <div className="footer-column">
            <h2 className="column-title">Countries</h2>
            <ul className="footer-links country-links">
              {countries.map((c) => (
                <li key={c.code}>
                  <Link href={`/${c.code}`} className="footer-link country-link-item">
                    <CountryFlag code={c.code} name={c.name} className="country-flag-image-xs" />
                    <span>{c.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="divider"></div>

        {/* Disclaimer Statement - Slanted Design */}
        <footer className="slanted-disclaimer">
          <strong>Financial Disclaimer:</strong> All calculators, tools, and content on <span className="slanted-brand">FinanceLives</span> are provided for educational and illustrative purposes only. They do not constitute financial, investment, legal, or tax advice. While we make every effort to ensure the accuracy of the calculations and data (including tax brackets and retirement contribution limits), actual values may vary depending on individual circumstances and changing government rules. Always consult a qualified professional before making any financial decisions.
        </footer>

        <div className="footer-copyright">
          <p className="copyright-text">
            &copy; {currentYear} <span className="slanted-brand">FinanceLives</span>. All rights reserved.
          </p>
        </div>
      </div>

      <style jsx>{`
        .footer-bg {
          background-color: var(--bg-surface);
          border-top: 1px solid var(--border-color);
          padding-top: var(--space-12);
          padding-bottom: var(--space-8);
          color: var(--text-secondary);
        }
        .footer-container {
          display: flex;
          flex-direction: column;
        }
        .footer-columns {
          display: grid;
          grid-template-columns: 1fr;
          gap: var(--space-8);
          margin-bottom: var(--space-8);
        }
        @media (min-width: 640px) {
          .footer-columns {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .footer-columns {
            grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
          }
        }
        .brand-column {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
        }
        .brand-description {
          font-size: var(--text-sm);
          line-height: 1.6;
          max-width: 320px;
          margin-bottom: 0;
        }
        .column-title {
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-primary);
          margin-bottom: var(--space-4);
        }
        .footer-links {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
        .footer-link {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          transition: color var(--transition-fast);
        }
        .footer-link:hover {
          color: var(--text-primary);
        }
        .footer-link-bold {
          font-weight: var(--fw-bold);
          color: var(--text-primary);
        }
        .footer-link-muted {
          font-size: var(--text-sm);
          color: var(--text-muted);
          cursor: default;
        }
        .footer-cookie-btn {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
        }
        .footer-category-label {
          font-size: var(--text-2xs);
          color: var(--text-muted);
          margin-top: var(--space-2);
          margin-bottom: var(--space-1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: var(--fw-semibold);
        }
        .country-links {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-2);
        }
        @media (min-width: 1024px) {
          .country-links {
            grid-template-columns: 1fr;
          }
        }
        .country-link-item {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }
        .divider {
          height: 1px;
          background-color: var(--border-color);
          margin-top: var(--space-8);
          margin-bottom: var(--space-8);
          width: 100%;
        }
        .slanted-disclaimer {
          padding: 0;
          margin: var(--space-2) 0 var(--space-8) 0;
          font-size: var(--text-2xs);
          color: var(--text-muted, var(--text-secondary));
          line-height: 1.5;
          font-style: italic;
          background: transparent;
          border: none;
        }
        .footer-copyright {
          display: flex;
          justify-content: center;
          align-items: center;
          padding-top: var(--space-4);
          border-top: 1px solid var(--border-color);
        }
        .copyright-text {
          font-size: var(--text-xs);
          color: var(--text-muted);
          margin-bottom: 0;
        }
      `}</style>
    </footer>
  );
}
